/**
 * Google Vids - MANUAL provider (spec sections 4, 5, 10, 15).
 *
 * Google Vids is a web application. It does not expose a documented
 * public video-generation API, and BEX LOOP will not invent one.
 *
 * What this provider does:
 *   - builds the optimised prompt
 *   - hands over a copy-to-clipboard brief and the official URL
 *   - records the manual generation in history
 *   - accepts the downloaded file back through normal import validation
 *
 * What this provider never does:
 *   - scrape Vids, call private endpoints, automate the browser
 *   - ask for or store a Google credential
 *   - claim that a clip was generated when a human has not made one
 */

import { VideoProvider, PROVIDER_STATUS } from './base.js';
import { RESEARCH } from '../core/config.js';
import { generateVidsPrompt } from '../prompts/prompt-generator.js';

export const GOOGLE_VIDS_URL = RESEARCH.googleVids.url;

export class GoogleVidsManualProvider extends VideoProvider {
  constructor() {
    super({
      id: 'google-vids',
      name: 'Google Vids - Manual',
      type: 'manual',
      description:
        'Google Vids is a first-class manual provider: BEX LOOP prepares the prompt and the brief, you generate the clip in Vids, then import the download.',
      docsUrl: 'https://blog.google/products-and-platforms/products/workspace/google-vids-updates-lyria-veo/',
      capabilities: {
        type: 'manual',
        automated: false,
        requiresApiKey: false,
        requiresWorker: false,
        manualStepRequired: true,
        asynchronous: false,
        supportsCancel: false,
        nativeAudio: true,
        durations: ['4', '6', '8'],
        aspectRatios: ['16:9', '9:16'],
        resolutions: ['720p', '1080p', '4k'],
        costModel: 'free-tier',
        costNote:
          'Verified 2026-04-02 from the official Google Workspace blog: personal Google accounts get 10 free video generations per month; Google AI Ultra / Workspace AI Ultra accounts can generate up to 1,000 Veo videos per month. Entitlements change - BEX LOOP never promises unlimited or permanently free access.',
        browserAutomation: false,
      },
    });
  }

  async status() {
    return {
      state: PROVIDER_STATUS.MANUAL,
      detail: 'Manual workflow - no API key required and none is ever requested.',
      checkedAt: new Date().toISOString(),
      meta: {
        providerUrl: GOOGLE_VIDS_URL,
        limits: RESEARCH.googleVids.notes,
        sources: RESEARCH.googleVids.sources,
        verifiedOn: RESEARCH.checkedOn,
      },
    };
  }

  async validateCredentials() {
    // There is nothing to validate: Vids is not called programmatically.
    return {
      configured: true,
      detail: 'No credential is required. BEX LOOP never authenticates to Google Vids.',
    };
  }

  /**
   * "Generate" for a manual provider means: produce the handoff brief.
   * It deliberately does NOT return an asset or claim a generation.
   */
  async generate({ scene, promptEntry = null, mood, style, durationSeconds, aspectRatio, resolution, loopMode }) {
    const prompt = generateVidsPrompt({
      scene,
      promptEntry,
      mood,
      style,
      durationSeconds,
      aspectRatio,
      resolution,
      loopMode,
      fps: 24,
    });

    return {
      mode: 'manual',
      providerId: this.id,
      sceneId: scene?.id ?? null,
      prompt,
      durationSeconds,
      aspectRatio,
      resolution,
      steps: [
        'Press COPY PROMPT.',
        'Press OPEN GOOGLE VIDS (the official site opens in a new tab).',
        'In Vids, create a new video and generate a clip with the prompt.',
        'Download the generated clip to your device.',
        'Return to BEX LOOP and import the file into this scene.',
        'Mark the loop quality (Seamless / Repeatable / Unknown).',
      ],
      providerUrl: GOOGLE_VIDS_URL,
      manualStepRequired: true,
      disclaimer:
        'No clip has been generated yet. BEX LOOP does not automate Google Vids, does not call undocumented endpoints, and does not request a Google credential.',
    };
  }

  async getGenerationStatus() {
    return {
      state: 'AWAITING_MANUAL_STEP',
      detail: 'Waiting for the operator to generate and download the clip in Google Vids.',
      done: false,
    };
  }

  async downloadResult() {
    return {
      downloaded: false,
      detail: 'Vids downloads happen in the browser, outside BEX LOOP. Use the import step instead.',
    };
  }

  async cancel() {
    return { cancelled: false, detail: 'Nothing to cancel: the generation happens in Google Vids.' };
  }

  /** Provider-specific research surfaced in the UI so nothing is hard-coded. */
  getResearch() {
    return {
      checkedOn: RESEARCH.checkedOn,
      notes: RESEARCH.googleVids.notes,
      sources: RESEARCH.googleVids.sources,
      url: GOOGLE_VIDS_URL,
    };
  }
}
